import React from 'react';
import { Users, Target, Calendar, TrendingUp } from 'lucide-react';
import EGAPRadarChart from './RadarChart';

interface MetricCardProps {
  title: string;
  value: string;
  change: string;
  changeLabel: string;
  icon: React.ElementType;
  iconColor: string;
  changeColor: string;
}

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  change,
  changeLabel,
  icon: Icon,
  iconColor,
  changeColor,
}) => {
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
          <div className="flex items-center mt-2">
            <span className={`text-sm font-medium ${changeColor}`}>{change}</span>
            <span className="text-sm text-gray-500 ml-1">{changeLabel}</span>
          </div>
        </div>
        <div className={`p-3 rounded-full ${iconColor}`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );
};

interface RecentActivityItem {
  team: string;
  action: string;
  time: string;
  score?: string;
}

const Dashboard: React.FC = () => {
  const recentActivities: RecentActivityItem[] = [
    {
      team: 'Engineering Team',
      action: 'Completed EGAP Assessment',
      time: '2 hours ago',
      score: '82/100',
    },
    {
      team: 'Marketing Team',
      action: 'Started Interview Process',
      time: '1 day ago',
    },
    {
      team: 'Sales Team',
      action: 'Assessment Results Ready',
      time: '2 days ago',
      score: '75/100',
    },
    {
      team: 'Product Team',
      action: 'Survey in Progress',
      time: '3 days ago',
    },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Culture Assessment Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Track organizational culture across Empathy, Growth, Adaptability, and Purpose
          </p>
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors">
          Start New Assessment
        </button>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricCard
          title="Team Participation"
          value="87%"
          change="+12% this"
          changeLabel="month"
          icon={Users}
          iconColor="bg-blue-500"
          changeColor="text-green-600"
        />
        <MetricCard
          title="Average EGAP Score"
          value="78.5"
          change="+5.2 points"
          changeLabel="Out of 100"
          icon={Target}
          iconColor="bg-green-500"
          changeColor="text-green-600"
        />
        <MetricCard
          title="Assessments This Month"
          value="23"
          change="+8 from last"
          changeLabel="month"
          icon={Calendar}
          iconColor="bg-purple-500"
          changeColor="text-green-600"
        />
        <MetricCard
          title="Improvement Trend"
          value="↗ 15%"
          change="6-month Strong upward"
          changeLabel="trajectory"
          icon={TrendingUp}
          iconColor="bg-orange-500"
          changeColor="text-green-600"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* EGAP Culture Assessment */}
        <div className="lg:col-span-2 bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">EGAP Culture Assessment</h2>
          <p className="text-gray-600 mb-6">Current scores across the four core cultural traits</p>
          
          {/* Radar Chart */}
          <div className="h-80">
            <EGAPRadarChart 
              data={{
                empathy: 75,
                growth: 82,
                adaptability: 68,
                purpose: 90
              }}
            />
          </div>

          {/* Trait Scores */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="text-center">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div className="bg-empathy h-2 rounded-full" style={{ width: '75%' }}></div>
              </div>
              <p className="text-sm font-medium text-gray-900">Empathy</p>
              <p className="text-xs text-gray-500">75/100</p>
            </div>
            <div className="text-center">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div className="bg-growth h-2 rounded-full" style={{ width: '82%' }}></div>
              </div>
              <p className="text-sm font-medium text-gray-900">Growth</p>
              <p className="text-xs text-gray-500">82/100</p>
            </div>
            <div className="text-center">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div className="bg-adaptability h-2 rounded-full" style={{ width: '68%' }}></div>
              </div>
              <p className="text-sm font-medium text-gray-900">Adaptability</p>
              <p className="text-xs text-gray-500">68/100</p>
            </div>
            <div className="text-center">
              <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                <div className="bg-purpose h-2 rounded-full" style={{ width: '90%' }}></div>
              </div>
              <p className="text-sm font-medium text-gray-900">Purpose</p>
              <p className="text-xs text-gray-500">90/100</p>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Recent Activity</h2>
          <p className="text-gray-600 mb-6">Latest assessments and team updates</p>
          
          <div className="space-y-4">
            {recentActivities.map((activity, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{activity.team}</p>
                  <p className="text-sm text-gray-600">{activity.action}</p>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-400">{activity.time}</p>
                    {activity.score && (
                      <span className="text-xs font-medium text-blue-600">{activity.score}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recommended Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Recommended Actions</h2>
        <p className="text-gray-600 mb-6">Based on your latest culture assessment results</p>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
            <h3 className="text-lg font-semibold text-purple-900 mb-2">Enhance Empathy</h3>
            <p className="text-purple-700 mb-4">
              Focus on improving interpersonal understanding and emotional intelligence
            </p>
            <button className="bg-purple-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-purple-700 transition-colors">
              View Action Plan
            </button>
          </div>
          
          <div className="bg-cyan-50 p-6 rounded-lg border border-cyan-200">
            <h3 className="text-lg font-semibold text-cyan-900 mb-2">Boost Adaptability</h3>
            <p className="text-cyan-700 mb-4">
              Strengthen team flexibility and change management capabilities
            </p>
            <button className="bg-cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-cyan-700 transition-colors">
              View Action Plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;